import { useSession } from "@src/appV2/Auth/api/useSession";
import { useCallback } from "react";

import { useSwitchUser } from "./useSwitchUser";

interface UseSwitchToAdminResult {
  /** True when the admin is currently impersonating a workplace/worker. */
  isImpersonating: boolean;
  /**
   * No-arg "return to admin view" — thin wrapper over `useSwitchUser`
   * that pulls `adminUserId` from the active session. Returns a Promise
   * so callers can await it for ordering; existing onClick consumers
   * pass it directly and let the Promise drop.
   */
  switchToAdmin: () => Promise<void>;
}

/**
 * Lightweight alternative to `useAccountSwitcher` for callers that only
 * need the "return to admin view" action — avoids spinning up the
 * account-search queries and debounced inputs that `useAccountSwitcher`
 * carries.
 */
export function useSwitchToAdmin(): UseSwitchToAdminResult {
  const { admin, adminUserId, userId } = useSession();
  const { switchUser } = useSwitchUser();

  const isImpersonating =
    admin &&
    typeof adminUserId === "string" &&
    typeof userId === "string" &&
    userId !== adminUserId;

  const switchToAdmin = useCallback(async () => {
    if (!adminUserId) {
      return;
    }
    await switchUser({ role: "admin", userId: adminUserId });
  }, [adminUserId, switchUser]);

  return { isImpersonating, switchToAdmin };
}
